import type { ChainType } from '@polaris/core/models'
import { webSupportedChainTypeKeys } from '@polaris/core/models'
import { isAddressUnique, isValidDetectedAddress } from '@polaris/core/utils'
import { useMemo, useState } from 'react'
import { useAsync } from 'react-use'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { cosmosChainPrefixesQuery } from '@/src/queries/keys/chains'
import { webChainDataProvider } from '@/src/services/chain-data-provider'

interface UseValidAddressInputParams {
  chainType?: ChainType
  chainId?: string
  initialValue?: string
  /**
   * External address state management. If provided, the hook will use this instead of internal state.
   */
  managedAddress?: string
  /**
   * External address setter. Required if managedAddress is provided.
   */
  setManagedAddress?: (address: string) => void
  /**
   * Optional list of addresses to exclude. If the input address matches any address in this list,
   * validation will be considered invalid. For Cosmos, matching is based on key hash equivalence
   * (bech32 prefix agnostic).
   */
  excludeAddresses?: string[]

  /**
   * Optional list of supplementary chain types to include in validation. If not provided, the default web supported chain types will be used.
   */
  supplementaryChainTypes?: ChainType[]
}

interface UseValidAddressInputReturn {
  address: string
  setAddress: (address: string) => void
  isValid: boolean | undefined
  isUnique: boolean
  loading: boolean
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  detectedChainType?: ChainType
}

/**
 * Hook for managing address input with real-time validation. Supports ChainType detection.
 *
 * @param chainType - The blockchain type to validate against. If not provided, will attempt to detect the chain type.
 * @param chainId - The specific chain ID (required for Cosmos chains)
 * @param initialValue - Initial address value (only used if managedAddress is not provided)
 * @param managedAddress - External address state (overrides internal state)
 * @param setManagedAddress - External address setter (required if managedAddress is provided)
 * @param excludeAddresses - Optional list of addresses to exclude. If the input address matches any address in this list (including across chains), validation will fail. If not provided, uniqueness check is skipped.
 * @param supplementaryChainTypes - Optional list of chain types to include in validation. If not provided, only the default web chain types will be used.
 * @returns Object with address state, validation status, and change handlers
 */
export function useValidAddressInput({
  chainType,
  chainId,
  initialValue = '',
  managedAddress,
  setManagedAddress,
  excludeAddresses,
  supplementaryChainTypes = [],
}: UseValidAddressInputParams = {}): UseValidAddressInputReturn {
  const [internalAddress, setInternalAddress] = useState(initialValue)

  // Use external state if provided, otherwise use internal state
  const address = managedAddress ?? internalAddress
  const setAddress = setManagedAddress ?? setInternalAddress

  const supportedChainTypes: ChainType[] = useMemo(
    () => [...webSupportedChainTypeKeys, ...supplementaryChainTypes],
    [supplementaryChainTypes],
  )

  // Address validation and optional chain-type auto-detection
  const { value: validationResult, loading } = useAsync(
    () =>
      isValidDetectedAddress({
        address,
        chainType,
        chainId,
        supportedChainTypes,
        getCosmosPrefixes: async () =>
          getQueryClient().ensureQueryData(
            cosmosChainPrefixesQuery(getQueryClient()),
          ),
        chainDataProvider: webChainDataProvider,
      }),
    [address, chainType, chainId],
  )

  // Compute exclusion matching after we have the detected chain type
  const isUnique = useMemo(
    () =>
      !excludeAddresses ||
      isAddressUnique({
        address,
        excludeAddresses,
        chainType: validationResult?.detectedChainType ?? chainType,
      }),
    [excludeAddresses, validationResult?.detectedChainType, chainType, address],
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value)
  }

  return {
    address,
    setAddress,
    isValid:
      validationResult?.isValid === false
        ? false
        : validationResult?.isValid === true
          ? isUnique
          : undefined,
    isUnique,
    loading,
    handleChange,
    detectedChainType: validationResult?.detectedChainType,
  }
}
